(function () {
  'use strict';

  /*
   * Collection-page price filter & sort for bundle products.
   *
   * Bundle-template products have variant.price = 0 in Shopify, so the native
   * price facet (slider) and price sort can't see their real "from" price. The
   * bundle app stores that price (in øre) in the product metafield
   * custom.from_price; this module makes the collection page use it.
   *
   * 1. Index — reads { handle -> fromPriceCents } from the server-rendered JSON
   *    template (templates/collection.price-index.liquid). Just metafield reads,
   *    no component fetching. Kept in memory for the page load only (no cache to
   *    go stale), so an app price change shows on the next page load.
   * 2. Slider — corrects the native price slider's max (which collapses to 0 when
   *    a collection is mostly bundles).
   * 3. Engine — when a price range is set OR the sort is by price, the engine
   *    detaches the native price inputs (so the server stops filtering bundles on
   *    variant.price = 0), loads the base set filtered by the OTHER facets, then
   *    filters/sorts client-side using the real price: bundles from the index,
   *    other products from their data-price (price_min) attribute.
   *
   * Public API:
   *   window.BundlePriceIndex.get(collectionHandle) -> Promise<{handle: cents}>
   *   window.BundlePriceIndex.getCurrent()          -> Promise<{handle: cents}>
   * Fires "bundle-price-index:ready" on document with detail { handle, prices }.
   */

  var inflight = {};  // collectionHandle -> in-flight build Promise (dedupe)
  var memIndex = {};  // collectionHandle -> prices, for THIS page load only

  // Money formatter — mirrors assets/bundle-listing-price.js so primed bundle
  // clones render an identical "from" price without any /products fetch.
  function formatMoney(cents) {
    var format = window.shopMoneyFormat || '${{amount}}';
    var amount = (cents || 0) / 100;
    function withDelimiters(value, precision, thousands, decimal) {
      if (precision === undefined) precision = 2;
      if (thousands === undefined) thousands = ',';
      if (decimal === undefined) decimal = '.';
      var parts = value.toFixed(precision).split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, ph) {
      switch (ph) {
        case 'amount': return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(amount, 0, ',');
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.');
        case 'amount_with_apostrophe_separator': return withDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(amount, 0, ' ');
        case 'amount_with_space_separator': return withDelimiters(amount, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(amount, 2, ' ', '.');
        default: return '';
      }
    });
  }

  function currentCollectionHandle() {
    var m = window.location.pathname.match(/\/collections\/([^\/?#]+)/);
    // Ignore the "all" pseudo-collection segment edge cases by just returning the slug.
    return m ? m[1] : null;
  }

  // --- index source: server-rendered JSON template -------------------------

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // Plain fetch with retry. No custom Accept header — sending one made the
  // storefront return a non-template body. Retries smooth over the transient
  // 502s that `shopify theme dev` throws under load.
  async function fetchTextWithRetry(url, attempts) {
    attempts = attempts || 5;
    var lastError;
    for (var i = 0; i < attempts; i++) {
      try {
        var res = await fetch(url);
        if (res.ok) return await res.text();
        lastError = new Error('HTTP ' + res.status);
        // Back off hard on rate limiting so retries don't make it worse.
        if (res.status === 429 && i < attempts - 1) { await delay(1000 * Math.pow(2, i)); continue; }
      } catch (e) {
        lastError = e;
      }
      if (i < attempts - 1) await delay(300 * (i + 1));
    }
    throw lastError;
  }

  async function fetchIndexPage(handle, page) {
    var url = '/collections/' + encodeURIComponent(handle) + '?view=price-index&page=' + page;
    var text = await fetchTextWithRetry(url);
    return JSON.parse(text);
  }

  async function fetchAllIndexItems(handle) {
    var firstPage = await fetchIndexPage(handle, 1);
    var items = (firstPage.items || []).slice();
    var pages = firstPage.pages || 1;
    if (pages > 1) {
      var requests = [];
      for (var p = 2; p <= pages; p++) {
        requests.push(fetchIndexPage(handle, p).catch(function () { return { items: [] }; }));
      }
      var results = await Promise.all(requests);
      results.forEach(function (r) { items = items.concat(r.items || []); });
    }
    return items;
  }

  // Run async worker over items with bounded concurrency (used by loadAllBaseCards).
  async function mapLimit(items, limit, worker) {
    var index = 0;
    var runners = [];
    for (var c = 0; c < Math.min(limit, items.length); c++) {
      runners.push((async function () {
        while (index < items.length) {
          var current = index++;
          await worker(items[current]);
        }
      })());
    }
    await Promise.all(runners);
  }

  // The index now carries the final from-price (øre) per bundle, written by the
  // app to metafield custom.from_price — no client-side component fetching.
  async function buildIndex(handle) {
    var items = await fetchAllIndexItems(handle);
    var prices = {};
    items.forEach(function (item) { prices[item.handle] = item.price || 0; });
    return prices;
  }

  // --- public getters (cached) --------------------------------------------

  // No persistent cache: the index is now just cheap metafield reads, so we keep
  // it in memory for this page load only (dedupes the few get() calls one page
  // makes). Every fresh page load re-reads the metafield, so an app price change
  // shows up immediately — no stale localStorage to clear.
  function get(handle) {
    if (!handle) return Promise.resolve({});
    if (memIndex[handle]) return Promise.resolve(memIndex[handle]);
    if (inflight[handle]) return inflight[handle];

    inflight[handle] = buildIndex(handle).then(function (prices) {
      memIndex[handle] = prices;
      delete inflight[handle];
      document.dispatchEvent(new CustomEvent('bundle-price-index:ready', {
        detail: { handle: handle, prices: prices }
      }));
      return prices;
    }).catch(function (e) {
      delete inflight[handle];
      throw e;
    });
    return inflight[handle];
  }

  function getCurrent() {
    return get(currentCollectionHandle());
  }

  window.BundlePriceIndex = { get: get, getCurrent: getCurrent };

  // --- slider range correction --------------------------------------------

  // Set the price slider's upper bound to the true max once we know the real
  // bundle prices. Re-applied after every facet re-render (the filters markup
  // is swapped out by ProductFacet._rerender).
  function applySliderRange(prices) {
    var values = Object.keys(prices).map(function (h) { return prices[h]; })
      .filter(function (c) { return typeof c === 'number' && c > 0; });
    if (!values.length) return;

    var maxKr = Math.ceil(Math.max.apply(null, values) / 100);

    document.querySelectorAll('price-range').forEach(function (priceRange) {
      var rangeInputs = priceRange.querySelectorAll('.price-range__range-group input');
      var numberInputs = priceRange.querySelectorAll('.price-range__input input');
      if (rangeInputs.length < 2 || numberInputs.length < 2) return;

      var higherRange = rangeInputs[1];
      var lowerNumber = numberInputs[0];
      var higherNumber = numberInputs[1];

      var existingMax = parseInt(higherRange.getAttribute('max'), 10) || 0;
      var newMax = Math.max(existingMax, maxKr);
      if (newMax <= existingMax && existingMax > 0) return; // already correct

      rangeInputs.forEach(function (input) { input.max = newMax; });
      lowerNumber.max = newMax;
      higherNumber.max = newMax;
      higherNumber.placeholder = newMax;

      // If the upper thumb sat at the old (wrong) max, stretch it to the new one.
      if (!higherNumber.value) {
        higherRange.value = newMax;
        var group = higherRange.parentElement;
        if (group) group.style.setProperty('--range-max', '100%');
      }
    });
  }

  // =========================================================================
  // Stage 2 — price filter takeover
  //
  // The native facet filters bundles on variant.price (= 0), so we detach the
  // price inputs from the form, let the server filter by the OTHER facets, and
  // apply price client-side using the index (bundles) + data-price (normals).
  // =========================================================================

  var S2 = {
    sectionId: null,
    prices: {},          // bundle handle -> cents (the index)
    baseCardsCache: {},  // base signature -> Promise<HTMLElement[]>
    priceActive: false,  // is a real price constraint applied?
    min: 0,              // kr (lower bound)
    max: Infinity,       // kr (upper bound)
    sortMode: null,      // 'price-ascending' | 'price-descending' | null
    observer: null
  };

  // The engine takes over rendering when there's a price constraint OR when the
  // active sort is by price (the native price sort ranks bundles at 0).
  function engaged() { return S2.priceActive || !!S2.sortMode; }

  function readSortMode() {
    var s = new URL(window.location).searchParams.get('sort_by');
    if (!s) {
      var form = facetFormEl();
      var hidden = form && form.querySelector('[name="sort_by"]');
      s = hidden ? hidden.value : null;
    }
    S2.sortMode = (s === 'price-ascending' || s === 'price-descending') ? s : null;
    return S2.sortMode;
  }

  function facetMainEl() { return document.getElementById('facet-main'); }
  function facetFormEl() { return document.getElementById('facet-filters-form'); }

  function getSectionId() {
    var pf = document.querySelector('product-facet');
    return pf ? pf.getAttribute('section-id') : null;
  }

  // Base params = current filter form (price already stripped) minus page.
  function baseParams() {
    var form = facetFormEl();
    var params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
    params.delete('page');
    return params;
  }

  function baseSignature(params) {
    var entries = Array.from(params.entries()).map(function (e) { return e[0] + '=' + e[1]; });
    entries.sort();
    return entries.join('&');
  }

  async function fetchSectionPage(params, page) {
    var qp = new URLSearchParams(params);
    qp.set('section_id', S2.sectionId);
    qp.set('page', page);
    var text = await fetchTextWithRetry(window.location.pathname + '?' + qp.toString());
    var div = document.createElement('div');
    div.innerHTML = text;
    return div;
  }

  function extractCards(div) {
    var inner = div.querySelector('.product-list__inner');
    return inner ? Array.from(inner.querySelectorAll(':scope > product-item')) : [];
  }

  function totalPagesFrom(div) {
    var max = 1;
    div.querySelectorAll('.pagination [data-page]').forEach(function (n) {
      var p = parseInt(n.getAttribute('data-page'), 10);
      if (p > max) max = p;
    });
    return max;
  }

  // Load every product card for the current base (non-price) criteria, across
  // all pages. Cached per base signature for the session.
  function loadAllBaseCards(params) {
    var sig = baseSignature(params);
    if (S2.baseCardsCache[sig]) return S2.baseCardsCache[sig];
    S2.baseCardsCache[sig] = (async function () {
      var firstDiv = await fetchSectionPage(params, 1);
      var cards = extractCards(firstDiv);
      var pages = totalPagesFrom(firstDiv);
      if (pages > 1) {
        var pageNums = [];
        for (var p = 2; p <= pages; p++) pageNums.push(p);
        var byPage = {};
        // Limit concurrency so we don't trip rate limits (esp. on theme dev).
        await mapLimit(pageNums, 3, async function (n) {
          try { byPage[n] = extractCards(await fetchSectionPage(params, n)); }
          catch (e) { byPage[n] = []; }
        });
        pageNums.forEach(function (n) { cards = cards.concat(byPage[n] || []); });
      }
      return cards;
    })();
    return S2.baseCardsCache[sig];
  }

  function priceOfCard(card) {
    if (card.getAttribute('data-bundle') === 'true') {
      var p = S2.prices[card.getAttribute('data-handle')];
      if (typeof p === 'number' && p > 0) return p;
    }
    return parseInt(card.getAttribute('data-price'), 10) || 0;
  }

  function updateCount(n) {
    var main = facetMainEl();
    var el = main && main.querySelector('.product-facet__meta-bar-item--count');
    if (el) el.textContent = el.textContent.replace(/[\d.\s,]*\d/, String(n));
  }

  function setPaginationHidden(hidden) {
    var main = facetMainEl();
    if (!main) return;
    main.querySelectorAll('.pagination').forEach(function (p) { p.style.display = hidden ? 'none' : ''; });
  }

  // Prime a cloned bundle card: show its from-price straight from the index and
  // remove the config script so bundle-listing-price.js won't re-fetch its
  // components (the main request-storm source when rendering many cards).
  function primeBundleClone(card) {
    if (card.getAttribute('data-bundle') !== 'true') return;
    var cfg = card.querySelector('script[data-bundle-config]');
    if (cfg) cfg.remove();
    var cents = S2.prices[card.getAttribute('data-handle')];
    if (typeof cents !== 'number' || cents <= 0) return;
    var priceEl = card.querySelector('[data-bundle-from-price]');
    if (!priceEl) return;
    var tpl = priceEl.dataset.bundleFromTpl || 'Fra __BUNDLE_PRICE__';
    priceEl.innerHTML = tpl.replace('__BUNDLE_PRICE__', formatMoney(cents));
  }

  // Remove reveal/reveal-visibility (opacity:0 / visibility:hidden) from a clone
  // and its descendants so injected cards show immediately.
  function revealNow(el) {
    el.removeAttribute('reveal');
    el.removeAttribute('reveal-visibility');
    el.querySelectorAll('[reveal], [reveal-visibility]').forEach(function (n) {
      n.removeAttribute('reveal');
      n.removeAttribute('reveal-visibility');
    });
  }

  function renderMatching(cards) {
    var main = facetMainEl();
    var inner = main && main.querySelector('.product-list__inner');
    if (!inner) return;
    var frag = document.createDocumentFragment();
    cards.forEach(function (c) {
      var clone = c.cloneNode(true);
      // Strip the entrance-animation attributes — cloned cards are never picked
      // up by the reveal observer, so [reveal] would leave them at opacity 0.
      revealNow(clone);
      primeBundleClone(clone);
      frag.appendChild(clone);
    });
    inner.innerHTML = '';
    inner.appendChild(frag);
    updateCount(cards.length);
    setPaginationHidden(true);
  }

  // Reuse the theme's native top loading bar.
  function showLoading(on) {
    document.documentElement.dispatchEvent(
      new CustomEvent(on ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  async function applyEngine() {
    if (!engaged()) return;
    showLoading(true);
    try {
      S2.prices = await getCurrent();

      // When we sort by price client-side the server's order is irrelevant, so
      // drop sort_by from the base request. That lets price-asc, price-desc and
      // slider tweaks all reuse ONE cached base set — instant after the first load.
      var params = baseParams();
      if (S2.sortMode) params.delete('sort_by');
      var cards = await loadAllBaseCards(params);

      // Filter by price (only if a constraint is set).
      if (S2.priceActive) {
        var minC = S2.min * 100;
        var maxC = (S2.max === Infinity) ? Infinity : S2.max * 100;
        cards = cards.filter(function (card) {
          var price = priceOfCard(card);
          return price >= minC && price <= maxC;
        });
      } else {
        cards = cards.slice();
      }

      // Sort by real price (bundles via index, normals via data-price) when the
      // active sort is price-based — the native sort ranks bundles at 0.
      if (S2.sortMode) {
        var dir = S2.sortMode === 'price-ascending' ? 1 : -1;
        cards.sort(function (a, b) { return (priceOfCard(a) - priceOfCard(b)) * dir; });
      }

      renderMatching(cards);
      reflectToUrl();
    } catch (e) {
      // Most likely a transient rate limit on theme dev — leave the grid as-is.
      console.warn('[bundle-price-filter] could not apply engine:', e && e.message);
    } finally {
      showLoading(false);
    }
  }

  // Re-fetch the native (price-free) page 1 and restore the standard paginated
  // grid + count when the price constraint is removed.
  async function restoreNative() {
    reflectToUrl();
    var div = await fetchSectionPage(baseParams(), 1);
    var fresh = div.querySelector('#facet-main');
    var main = facetMainEl();
    if (fresh && main) {
      pauseObserver();
      main.innerHTML = fresh.innerHTML;
      resumeObserver();
    }
  }

  function reflectToUrl() {
    var url = new URL(window.location);
    if (S2.priceActive) {
      url.searchParams.set('bpf_min', S2.min);
      if (S2.max !== Infinity) url.searchParams.set('bpf_max', S2.max);
      else url.searchParams.delete('bpf_max');
    } else {
      url.searchParams.delete('bpf_min');
      url.searchParams.delete('bpf_max');
    }
    history.replaceState({}, '', url);
  }

  // --- price input wiring --------------------------------------------------

  function priceInputs() {
    var pr = document.querySelector('price-range');
    if (!pr) return null;
    var numbers = pr.querySelectorAll('.price-range__input input');
    var ranges = pr.querySelectorAll('.price-range__range-group input');
    if (numbers.length < 2 || ranges.length < 2) return null;
    return { numbers: numbers, ranges: ranges, lowerNum: numbers[0], higherNum: numbers[1], lowerRange: ranges[0], higherRange: ranges[1] };
  }

  function readPriceState() {
    var io = priceInputs();
    if (!io) { S2.priceActive = false; return; }
    var lo = parseInt(io.lowerNum.value, 10);
    var hi = parseInt(io.higherNum.value, 10);
    var maxAttr = parseInt(io.higherRange.max, 10) || Infinity;
    S2.min = isNaN(lo) ? 0 : lo;
    S2.max = isNaN(hi) ? Infinity : hi;
    S2.priceActive = (S2.min > 0) || (S2.max !== Infinity && S2.max < maxAttr);
  }

  function writePriceInputs() {
    var io = priceInputs();
    if (!io) return;
    var maxAttr = parseInt(io.higherRange.max, 10) || 0;
    if (S2.min > 0) {
      io.lowerNum.value = S2.min;
      io.lowerRange.value = S2.min;
      if (maxAttr) io.lowerRange.parentElement.style.setProperty('--range-min', (S2.min / maxAttr * 100) + '%');
    }
    if (S2.max !== Infinity) {
      io.higherNum.value = S2.max;
      io.higherRange.value = S2.max;
      if (maxAttr) io.higherRange.parentElement.style.setProperty('--range-max', (S2.max / maxAttr * 100) + '%');
    }
  }

  // Detach price number inputs from the form (so the server never price-filters
  // bundles) and wire our own change handler. Idempotent across re-renders.
  function detachPriceInputs() {
    var io = priceInputs();
    if (!io) return;
    [io.lowerNum, io.higherNum].forEach(function (input) {
      if (input.name) { input.dataset.bpfName = input.name; input.removeAttribute('name'); }
    });
    [io.lowerNum, io.higherNum, io.lowerRange, io.higherRange].forEach(function (input) {
      if (!input.dataset.bpfWired) {
        input.dataset.bpfWired = '1';
        input.addEventListener('change', onPriceChanged);
      }
    });
  }

  var priceDebounce = null;
  function onPriceChanged() {
    clearTimeout(priceDebounce);
    priceDebounce = setTimeout(function () {
      readPriceState();
      readSortMode();
      if (engaged()) applyEngine();
      else restoreNative();
    }, 250);
  }

  // --- re-render handling --------------------------------------------------

  function onNativeRerender() {
    // The theme replaced #facet-main (and the filters markup) for a new base
    // criteria (other filter, sort, or pagination). Re-correct the slider,
    // re-detach inputs, restore our state, re-read the sort, and re-apply the
    // engine on the new base set.
    detachPriceInputs();
    applySliderRange(S2.prices);
    writePriceInputs();
    readSortMode();
    if (engaged()) applyEngine();
  }

  function pauseObserver() { if (S2.observer) S2.observer.disconnect(); }
  function resumeObserver() {
    var main = facetMainEl();
    if (S2.observer && main) S2.observer.observe(main, { childList: true });
  }

  function restoreFromUrl() {
    var u = new URL(window.location);
    var mn = u.searchParams.get('bpf_min');
    var mx = u.searchParams.get('bpf_max');
    if (mn === null && mx === null) return false;
    S2.min = mn !== null ? parseInt(mn, 10) : 0;
    S2.max = mx !== null ? parseInt(mx, 10) : Infinity;
    S2.priceActive = (S2.min > 0) || (S2.max !== Infinity);
    return true;
  }

  // Adopt a native ?filter.v.price.* URL (direct link, bookmark, or a submit
  // that slipped through) into our client-side filter, and strip the native
  // params so the server stops (wrongly) price-filtering bundles.
  function adoptNativePriceParams() {
    var u = new URL(window.location);
    var gte = u.searchParams.get('filter.v.price.gte');
    var lte = u.searchParams.get('filter.v.price.lte');
    if (gte === null && lte === null) return false;
    S2.min = gte !== null ? parseInt(gte, 10) : 0;
    S2.max = lte !== null ? parseInt(lte, 10) : Infinity;
    S2.priceActive = (S2.min > 0) || (S2.max !== Infinity);
    u.searchParams.delete('filter.v.price.gte');
    u.searchParams.delete('filter.v.price.lte');
    history.replaceState({}, '', u);
    return true;
  }

  // --- init ----------------------------------------------------------------

  function init() {
    if (!currentCollectionHandle()) return;
    if (!document.querySelector('price-range')) return; // no price filter here

    S2.sectionId = getSectionId();
    detachPriceInputs();
    adoptNativePriceParams() || restoreFromUrl();
    readSortMode();

    // "Clear filters" must also drop the price constraint (sort is preserved).
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-action="clear-filters"]')) {
        S2.priceActive = false; S2.min = 0; S2.max = Infinity;
      }
    }, true);

    // Watch for native facet re-renders (other filters / sort / pagination).
    S2.observer = new MutationObserver(onNativeRerender);
    resumeObserver();

    var runIdle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 200); };
    runIdle(function () {
      get(currentCollectionHandle()).then(function (prices) {
        S2.prices = prices;
        applySliderRange(prices);
        writePriceInputs();
        if (engaged()) applyEngine();
      }).catch(function () { /* leave native behaviour on failure */ });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
